import React, { useEffect, useState } from "react";
import { message, Modal, Spin, Tag } from "antd";
import { MODAL_STYLES, MODAL_TITLE_STYLE } from "../../../../styles/modalStyles";
import { LeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import EntityService, { CreateEntityDto, EntityDto } from "../../services/EntityService";
import TieringService from "../../../TieringView/services/TieringService";
import { EntityReltnDto } from "../../../TieringView/types/tiering.types";
import ProjectService, { ProjectDto } from "../../../ProjectView/services/ProjectService";
import PartnerRoster from "../../../TieringView/components/PartnerRoster/PartnerRoster";
import { ComingSoon } from "../../../../shared/base-components/ComingSoon/ComingSoon";
import styles from "./EntityDetails.module.css";

// Fast path: entity object carried via React Router location.state when navigating from EntityList.
// Fallback: fetch GET /api/project/entities and find by id (no GET /api/entity/:id endpoint).

const projectService = new ProjectService();

const DETAIL_TABS = [
  { key: "ownership",    label: "Ownership"    },
  { key: "partners",     label: "Partners"     },
  { key: "projects",     label: "Projects"     },
  { key: "engagements",  label: "Engagements"  },
  { key: "auditLog",     label: "Audit Log"    },
];

// ── Not-found state ───────────────────────────────────────────────────────────

const NotFound: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className={styles.pageWrap}>
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>🔍</div>
        <div className={styles.emptyText}>Entity not found</div>
        <div className={styles.emptySubtext}>No entity with this ID was found in your tenant.</div>
        <button className={styles.btnOutline} onClick={() => navigate("/entities")}>
          Back to Entities
        </button>
      </div>
    </div>
  );
};

// ── Ownership sub-tab — GET /api/tiering/relationships/{entityId} ─────────────

const OwnershipTab: React.FC<{ entityId: number }> = ({ entityId }) => {
  const navigate = useNavigate();
  const [relationships, setRelationships] = useState<EntityReltnDto[]>([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(false);

  useEffect(() => {
    let cancelled = false;
    TieringService.getRelationships(entityId)
      .then(data => { if (!cancelled) setRelationships(data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entityId]);

  if (loading) return <div className={styles.tabLoadingState}><Spin /></div>;
  if (error) return (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>⚠️</div>
      <div className={styles.emptyText}>Failed to load ownership data</div>
    </div>
  );
  if (relationships.length === 0) return (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>🌐</div>
      <div className={styles.emptyText}>No ownership relationships</div>
      <div className={styles.emptySubtext}>
        No tiering relationships are defined for this entity.
        Use the Tiering module to define ownership structure.
      </div>
      <button
        className={styles.btnOutlineAccent}
        style={{ marginTop: 16 }}
        onClick={() => navigate(`/tiering/${entityId}`)}
      >
        Open in Tiering View →
      </button>
    </div>
  );

  return (
    <div className={styles.dataTableWrap}>
      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>Partner Name</th>
            <th>Relationship</th>
            <th style={{ width: 130 }}>Ownership %</th>
            <th style={{ width: 100 }}>Tier Level</th>
          </tr>
        </thead>
        <tbody>
          {relationships.map(r => (
            <tr key={r.id}>
              <td className={styles.cellName}>{r.endEntityName}</td>
              <td className={styles.cellMuted}>{r.relationshipDirection || "—"}</td>
              <td>
                {r.ownershipPercent != null
                  ? <span className={styles.ownershipPct}>{r.ownershipPercent}%</span>
                  : <span className={styles.cellMuted}>—</span>
                }
              </td>
              <td className={styles.cellMuted}>{r.tierLevel != null ? `Level ${r.tierLevel}` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Projects sub-tab ──────────────────────────────────────────────────────────

const ProjectsTab: React.FC<{ entityId: number }> = ({ entityId }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(false);

  useEffect(() => {
    let cancelled = false;
    projectService.getProjects({ entityId, itemsPerPage: 50, startIndex: 0 })
      .then(res => { if (!cancelled) setProjects(Array.isArray(res.items) ? res.items : []); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entityId]);

  if (loading) return <div className={styles.tabLoadingState}><Spin /></div>;
  if (error) return (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>⚠️</div>
      <div className={styles.emptyText}>Failed to load projects</div>
    </div>
  );
  if (projects.length === 0) return (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>📁</div>
      <div className={styles.emptyText}>No projects yet</div>
      <div className={styles.emptySubtext}>No projects are linked to this entity.</div>
    </div>
  );

  return (
    <div className={styles.dataTableWrap}>
      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>Project Name</th>
            <th>Tax Year</th>
            <th>Type</th>
            <th>Status</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {projects.map(p => (
            <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)} style={{ cursor: "pointer" }}>
              <td className={styles.cellName}>{p.name}</td>
              <td>{p.taxYear}</td>
              <td>{p.engagementType}</td>
              <td>
                <Tag style={{
                  color: p.status === "Active" ? "#22c55e" : "#f59e0b",
                  borderColor: p.status === "Active" ? "rgba(34,197,94,0.4)" : "rgba(245,158,11,0.4)",
                  background: "transparent",
                  borderRadius: 4, fontSize: 11, fontWeight: 500,
                }}>
                  {p.status}
                </Tag>
              </td>
              <td className={styles.cellMuted}>
                {p.dateCreated ? new Date(p.dateCreated).toLocaleDateString() : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Edit modal (inline) ────────────────────────────────────────────────────────

const CATEGORIES_PARTNERSHIP = ['Limited Partnership', 'LLC', 'S-Corporation', 'C-Corporation', 'General Partnership'];
const CATEGORIES_PARTNER = ['Individual', 'Trust', 'Corporation', 'Partnership'];

const EditModal: React.FC<{
  entity: EntityDto;
  onClose: () => void;
  onSaved: () => void;
}> = ({ entity, onClose, onSaved }) => {
  const entityTypeLower = entity.entityType?.toLowerCase() === 'partner' ? 'partner' : 'partnership';

  const [form, setForm] = useState({
    name:     entity.name ?? '',
    category: entity.category ?? '',
    address:  entity.address ?? '',
    city:     entity.city ?? '',
    state:    entity.state ?? '',
    zipCode:  entity.zipCode ?? '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(prev => ({ ...prev, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) { message.error("Name is required"); return; }
    setSaving(true);
    const dto: CreateEntityDto = {
      name:     form.name.trim(),
      entityType: entityTypeLower,
      category: form.category,
      address:  form.address,
      city:     form.city,
      state:    form.state,
      zipCode:  form.zipCode,
    };
    try {
      await EntityService.updateEntity(entity.id, dto);
      message.success("Entity updated");
      onSaved();
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : "An error occurred";
      message.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const categories = entityTypeLower === 'partner' ? CATEGORIES_PARTNER : CATEGORIES_PARTNERSHIP;

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={<span style={MODAL_TITLE_STYLE}>Edit Entity</span>}
      centered
      width={520}
      styles={MODAL_STYLES}
    >
      <div className={styles.editFormStack}>
        <div className={styles.editFormRow}>
          <label className={styles.editFormLabel}>Entity Name <span className={styles.req}>*</span></label>
          <input
            className={styles.editFormInput}
            value={form.name}
            onChange={set('name')}
            placeholder="Entity name"
          />
        </div>
        <div className={styles.editFormRow}>
          <label className={styles.editFormLabel}>Category</label>
          <select className={styles.editFormInput} value={form.category} onChange={set('category')} style={{ height: 36 }}>
            {categories.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className={styles.editFormRow}>
          <label className={styles.editFormLabel}>Street Address</label>
          <input className={styles.editFormInput} value={form.address} onChange={set('address')} placeholder="123 Main St" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px', gap: 10 }}>
          <div className={styles.editFormRow}>
            <label className={styles.editFormLabel}>City</label>
            <input className={styles.editFormInput} value={form.city} onChange={set('city')} placeholder="New York" />
          </div>
          <div className={styles.editFormRow}>
            <label className={styles.editFormLabel}>State</label>
            <input className={styles.editFormInput} value={form.state} onChange={set('state')} placeholder="NY" maxLength={2} />
          </div>
          <div className={styles.editFormRow}>
            <label className={styles.editFormLabel}>ZIP</label>
            <input className={styles.editFormInput} value={form.zipCode} onChange={set('zipCode')} placeholder="10001" />
          </div>
        </div>
        <div className={styles.editFormActions}>
          <button className={styles.btnOutline} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ── EntityDetails ─────────────────────────────────────────────────────────────

export const EntityDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id }   = useParams<{ id: string }>();

  const stateEntity: EntityDto | undefined = location.state?.entity;
  const initialTab: string | undefined = location.state?.initialTab;

  const [resolvedEntity, setResolvedEntity] = useState<EntityDto | null>(stateEntity ?? null);
  const [isFetching, setIsFetching]         = useState(!stateEntity);
  const [activeTab, setActiveTab]           = useState(initialTab ?? "ownership");
  const [showEdit, setShowEdit]             = useState(false);

  useEffect(() => {
    if (stateEntity) return;
    let cancelled = false;
    setIsFetching(true);
    const numericId = parseInt(id ?? "", 10);
    EntityService.getEntityById(numericId)
      .then(entity => { if (!cancelled) setResolvedEntity(entity); })
      .catch(() => { if (!cancelled) setResolvedEntity(null); })
      .finally(() => { if (!cancelled) setIsFetching(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (isFetching) {
    return <div className={styles.pageWrap}><div className={styles.loadingState}><Spin /></div></div>;
  }
  if (!resolvedEntity) return <NotFound />;

  const entity = resolvedEntity;
  const entityCode = `ENT-${String(entity.id).padStart(3, "0")}`;
  const isPartnership = entity.entityType === "Partnership";
  const visibleTabs = DETAIL_TABS.filter(t => t.key !== "partners" || isPartnership);

  const handleDeactivate = () => {
    Modal.confirm({
      title: "Deactivate entity?",
      content: `This will mark "${entity.name}" as inactive. You can reactivate it later.`,
      okText: "Deactivate",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await EntityService.deactivateEntity(entity.id);
          message.success("Entity deactivated");
          navigate("/entities");
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : "An error occurred";
          message.error(errMsg);
        }
      },
    });
  };

  return (
    <div className={styles.pageWrap}>

      {/* ── Back link ── */}
      <button className={styles.backLink} onClick={() => navigate("/entities")}>
        <LeftOutlined style={{ marginRight: 6, fontSize: 12 }} />
        Back to Entities
      </button>

      {/* ── Header ── */}
      <div className={styles.detailHead}>
        <div className={styles.headLeft}>
          <div className={styles.titleRow}>
            <h1 className={styles.pageTitle}>{entity.name}</h1>
            {entity.entityType && (
              <span className={
                entity.entityType === "Partnership" ? styles.badgePartnership : styles.badgePartner
              }>
                {entity.entityType}
              </span>
            )}
            {entity.status && (
              <span className={entity.status === "Active" ? styles.badgeActive : styles.badgeInactive}>
                {entity.status}
              </span>
            )}
          </div>
          <div className={styles.metaLine}>
            <span className={styles.mono}>{entityCode}</span>
            {entity.clientName && (
              <>
                <span className={styles.metaSep}>·</span>
                <span className={styles.metaMuted}>{entity.clientName}</span>
              </>
            )}
          </div>
        </div>
        <div className={styles.headRight}>
          <button className={styles.btnOutline} onClick={() => setShowEdit(true)}>
            Edit
          </button>
          <button className={styles.btnDanger} onClick={handleDeactivate}>
            Deactivate
          </button>
        </div>
      </div>

      {/* ── Info cards ── */}
      <div className={styles.infoGrid}>

        <div className={styles.infoCard}>
          <div className={styles.infoCardTitle}>Entity Details</div>
          <div className={styles.fieldGrid}>
            <div className={styles.fieldItem}>
              <div className={styles.fieldLabel}>Name</div>
              <div className={styles.fieldValue}>{entity.name}</div>
            </div>
            <div className={styles.fieldItem}>
              <div className={styles.fieldLabel}>Entity ID</div>
              <div className={styles.fieldValue}><span className={styles.mono}>{entityCode}</span></div>
            </div>
            <div className={styles.fieldItem}>
              <div className={styles.fieldLabel}>Entity Type</div>
              <div className={styles.fieldValue}>{entity.entityType || "—"}</div>
            </div>
            <div className={styles.fieldItem}>
              <div className={styles.fieldLabel}>Status</div>
              <div className={styles.fieldValue}>{entity.status || "—"}</div>
            </div>
            <div className={styles.fieldItem}>
              <div className={styles.fieldLabel}>EIN / SSN</div>
              <div className={styles.fieldValue}>
                <span className={styles.mono}>{entity.einSsn || "—"}</span>
              </div>
            </div>
            <div className={styles.fieldItem}>
              <div className={styles.fieldLabel}>Category</div>
              <div className={styles.fieldValue}>{entity.category || "—"}</div>
            </div>
          </div>
        </div>

        <div className={styles.infoCard}>
          <div className={styles.infoCardTitle}>Location &amp; System</div>
          <div className={styles.fieldGrid}>
            <div className={entity.taxYearStart ? styles.fieldItem : styles.fieldItemGap}>
              <div className={styles.fieldLabel}>Tax Year Start</div>
              <div className={entity.taxYearStart ? styles.fieldValue : styles.fieldValueGap}>
                {entity.taxYearStart || "—"}
              </div>
            </div>
            <div className={entity.createdAt ? styles.fieldItem : styles.fieldItemGap}>
              <div className={styles.fieldLabel}>Date Created</div>
              <div className={entity.createdAt ? styles.fieldValue : styles.fieldValueGap}>
                {entity.createdAt ? new Date(entity.createdAt).toLocaleDateString() : "—"}
              </div>
            </div>
            <div className={entity.city ? styles.fieldItem : styles.fieldItemGap}>
              <div className={styles.fieldLabel}>City</div>
              <div className={entity.city ? styles.fieldValue : styles.fieldValueGap}>
                {entity.city || "—"}
              </div>
            </div>
            <div className={entity.state ? styles.fieldItem : styles.fieldItemGap}>
              <div className={styles.fieldLabel}>State</div>
              <div className={entity.state ? styles.fieldValue : styles.fieldValueGap}>
                {entity.state || "—"}
              </div>
            </div>
            <div className={entity.address ? styles.fieldItem : styles.fieldItemGap}>
              <div className={styles.fieldLabel}>Address</div>
              <div className={entity.address ? styles.fieldValue : styles.fieldValueGap}>
                {entity.address || "—"}
              </div>
            </div>
            <div className={entity.clientName ? styles.fieldItem : styles.fieldItemGap}>
              <div className={styles.fieldLabel}>Client</div>
              <div className={entity.clientName ? styles.fieldValue : styles.fieldValueGap}>
                {entity.clientName || "—"}
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ── Tabs ── */}
      <div className={styles.tabs}>
        {visibleTabs.map(t => (
          <button
            key={t.key}
            className={activeTab === t.key ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}
      <div className={styles.tabContent}>
        {activeTab === "ownership" && (
          <OwnershipTab entityId={entity.id} />
        )}
        {activeTab === "partners" && isPartnership && (
          <PartnerRoster partnershipId={entity.id} />
        )}
        {activeTab === "projects" && (
          <ProjectsTab entityId={entity.id} />
        )}
        {activeTab === "engagements" && (
          <ComingSoon title="Engagements" />
        )}
        {activeTab === "auditLog" && (
          <ComingSoon title="Audit Log" />
        )}
      </div>

      {/* ── Edit modal ── */}
      {showEdit && (
        <EditModal
          entity={entity}
          onClose={() => setShowEdit(false)}
          onSaved={() => {
            setShowEdit(false);
            EntityService.getEntityById(entity.id)
              .then(updated => setResolvedEntity(updated))
              .catch(() => {});
          }}
        />
      )}

    </div>
  );
};
